import type { Trade } from "@trader/backtester"
import {
  createSeriesMarkers,
  type IChartApi,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type SeriesMarker,
  type SeriesType,
  type Time,
  type UTCTimestamp
} from "lightweight-charts"
import type { IndicatorRegistryEntry } from "lightweight-charts-indicators"
import type { IndicatorRenderer } from "lightweight-charts-indicators/render"
import { MarkerTips } from "../chart/markerTips.ts"
import { TradeBoxesPrimitive } from "../chart/tradeBoxPrimitive.ts"
import type { IndicatorModules } from "../indicators/indicatorModules.ts"
import { formatMoney, formatPercent, formatPrice } from "./format.ts"
import type { StrategyResult } from "./protocol.ts"
import { riskReward, tradeBoxes } from "./tradeBoxes.ts"

const LONG = "#2962ff"
const SHORT = "#e91e63"

/** Report times are epoch milliseconds; the chart's are seconds. */
export const legTime = (time: number): UTCTimestamp => Math.round(time / 1000) as UTCTimestamp

const signedQty = (trade: Trade, entry: boolean) => {
  const sign = (trade.direction === "long") === entry ? "+" : "−"
  return `${sign}${Number(trade.qty.toFixed(6))}`
}

/** The id of a trade's entry or exit mark: its label shows when the pointer is on it. */
const markerId = (index: number, leg: "entry" | "exit") => `trade-${index}-${leg}`

/**
 * TradingView's trade marks: an entry is an arrow at the bar (▲ below for long,
 * ▼ above for short) with its signal and size; an exit is the opposite arrow
 * with its signal. Open trades have no exit mark.
 */
export const tradeMarkers = (trades: ReadonlyArray<Trade>): Array<SeriesMarker<Time>> => {
  const markers: Array<SeriesMarker<Time>> = []
  for (const [index, trade] of trades.entries()) {
    const long = trade.direction === "long"
    markers.push({
      id: markerId(index, "entry"),
      time: legTime(trade.entry.time),
      position: long ? "belowBar" : "aboveBar",
      shape: long ? "arrowUp" : "arrowDown",
      color: long ? LONG : SHORT,
      text: `${trade.entry.signal} ${signedQty(trade, true)}`
    })
    if (trade.open) continue
    markers.push({
      id: markerId(index, "exit"),
      time: legTime(trade.exit.time),
      position: long ? "aboveBar" : "belowBar",
      shape: long ? "arrowDown" : "arrowUp",
      color: long ? SHORT : LONG,
      text: `${trade.exit.signal} ${signedQty(trade, false)}`
    })
  }
  return markers.sort((a, b) => (a.time as number) - (b.time as number))
}

/**
 * The label of each trade mark: the side, size and fill price, then the signal, and the entry's
 * planned stop-loss / take-profit or the exit's profit.
 */
export const tradeTips = (trades: ReadonlyArray<Trade>, currency: string): Map<string, string> => {
  const tips = new Map<string, string>()
  const qty = (trade: Trade) => Number(trade.qty.toFixed(6))
  for (const [index, trade] of trades.entries()) {
    const long = trade.direction === "long"
    const side = long ? "Long" : "Short"
    const levels = [
      trade.stopLoss != null ? `SL ${formatPrice(trade.stopLoss)}` : null,
      trade.takeProfit != null ? `TP ${formatPrice(trade.takeProfit)}` : null,
      riskReward(trade) !== null ? `R:R ${riskReward(trade)}` : null
    ].filter((part) => part !== null)
    tips.set(
      markerId(index, "entry"),
      [
        `${side} entry: ${long ? "buy" : "sell"} ${qty(trade)} @ ${formatPrice(trade.entry.price)}`,
        trade.entry.signal,
        levels.join(" · ") || null,
        trade.open ? `Open, P&L ${formatMoney(trade.profit, currency)}` : null
      ].filter((line) => line !== null && line !== "").join("\n")
    )
    if (trade.open) continue
    tips.set(
      markerId(index, "exit"),
      [
        `${side} exit: ${long ? "sell" : "buy"} ${qty(trade)} @ ${formatPrice(trade.exit.price)}`,
        trade.exit.signal,
        `P&L ${formatMoney(trade.profit, currency)} (${formatPercent(trade.profitPercent)})`
      ].filter((line) => line !== "").join("\n")
    )
  }
  return tips
}

/**
 * A strategy on one chart pane: its trade marks on the price series, and its own
 * plots (e.g. the averages of an MA crossover) drawn like an indicator. Plain
 * TypeScript; ChartPane owns its lifetime.
 */
export class StrategyLayer {
  private readonly chart: IChartApi
  private readonly mainSeries: ISeriesApi<SeriesType>
  private readonly markers: ISeriesMarkersPluginApi<Time>
  private readonly boxes = new TradeBoxesPrimitive()
  private readonly tips: MarkerTips
  /** Draws the strategy's plots once the indicator code has loaded (marks don't need it). */
  private renderer: IndicatorRenderer | null = null

  constructor(chart: IChartApi, mainSeries: ISeriesApi<SeriesType>) {
    this.chart = chart
    this.mainSeries = mainSeries
    this.markers = createSeriesMarkers(mainSeries, [])
    mainSeries.attachPrimitive(this.boxes)
    this.tips = new MarkerTips(chart)
  }

  /** Called once the indicator code is loaded; plots show from the next `show`. */
  setModules(modules: IndicatorModules): void {
    // Below the indicators' panes, so a strategy oscillator doesn't reshuffle them.
    this.renderer ??= new modules.IndicatorRenderer(this.chart, { paneIndex: 9, mainSeries: this.mainSeries })
  }

  show(result: StrategyResult, bars: ReadonlyArray<{ time: number }>, visible: boolean): void {
    const trades = visible ? result.report?.trades ?? [] : []
    this.markers.setMarkers(tradeMarkers(trades))
    this.boxes.setBoxes(tradeBoxes(trades, result.report?.currency ?? ""))
    this.tips.set(tradeTips(trades, result.report?.currency ?? ""))
    this.renderer?.clear()
    if (!visible || this.renderer === null || result.plots === undefined) return
    try {
      const entry = result.plots.entry as unknown as IndicatorRegistryEntry
      this.renderer.render(entry, result.plots.result as never, bars.map((bar) => ({ ...bar })) as never, {})
    } catch {
      // A plot the renderer can't draw shouldn't hide the trades.
    }
  }

  clear(): void {
    this.markers.setMarkers([])
    this.boxes.setBoxes([])
    this.tips.set(new Map())
    this.renderer?.clear()
  }

  dispose(): void {
    this.markers.detach()
    this.mainSeries.detachPrimitive(this.boxes)
    this.tips.dispose()
    this.renderer?.clear()
  }
}
